import { HEARING_AID_PRODUCT_TYPE } from "constants/sales";
import type { LineItem, Sale } from "services/types/sales.type";
import { formatDateOnlyValue } from "utils/common";

import { isReturnedOrExchanged } from "./common/utils";

/**
 * Gets the ear from a patient_product entry.
 * Checks both direct ear property and inventory_product.ear.
 */
export const getPatientProductEar = (pp: any): string | undefined => {
  return pp?.ear || pp?.inventory_product?.ear;
};

/**
 * The ear a line item is fitted to, checked in multiple locations (matching the
 * line-item-row.component.tsx pattern).
 */
export const getLineItemEar = (item: LineItem): string | undefined => {
  const patientProducts = (item as any).patient_products;
  const orderItem = (item as any).order_item;

  return (
    item.inventory_product?.ear ??
    (Array.isArray(patientProducts)
      ? getPatientProductEar(patientProducts[0])
      : undefined) ??
    orderItem?.patient_product?.ear
  );
};

/**
 * Finds the first hearing aid line item from a sale for a specific ear.
 * Used for individual hearing aid merge tags (not table-based).
 */
export const getFirstHearingAidFromSale = (
  sale: Sale | undefined,
  earType: string
): LineItem | undefined => {
  if (!sale?.items) return undefined;

  return (sale.items as LineItem[]).find((item) => {
    if (isReturnedOrExchanged(item)) {
      return false;
    }
    if (item.product?.master_product?.type !== HEARING_AID_PRODUCT_TYPE) {
      return false;
    }

    return getLineItemEar(item) === earType;
  });
};

/**
 * Gets the serial number from a line item, checking multiple possible locations.
 * Matches pattern from line-item-row.component.tsx
 */
export const getHearingAidSerialNumber = (
  item: LineItem | undefined,
  earType: string
): string => {
  if (!item) return "";

  const patientProducts = (item as any).patient_products;
  const orderItem = (item as any).order_item;

  // Check patient_products array first (matching line-item-row pattern)
  if (Array.isArray(patientProducts)) {
    for (const pp of patientProducts) {
      if (
        getPatientProductEar(pp) === earType &&
        pp.inventory_product?.serial_number
      ) {
        return pp.inventory_product.serial_number;
      }
    }
  }

  // Check direct inventory_product
  if (item.inventory_product?.serial_number) {
    return item.inventory_product.serial_number;
  }

  // Check order_item.patient_product
  if (orderItem?.patient_product?.serial_number) {
    return orderItem.patient_product.serial_number;
  }

  return item.serial_number ?? "";
};

/**
 * Gets the warranty start date (delivered_at) from a line item.
 */
export const getHearingAidWarrantyStart = (
  item: LineItem | undefined,
  _earType: string
): string => {
  if (!item) return "";

  const deliveredAt = item.delivered_at;
  return deliveredAt ? formatDateOnlyValue(deliveredAt, "MM/dd/yyyy") : "";
};

/**
 * Gets the delivery (fitting) date from a line item.
 * Delivery date and warranty start date are the same field (delivered_at);
 * this alias exists so templates can use semantically meaningful tag names.
 */
export const getHearingAidDeliveryDate = (
  item: LineItem | undefined,
  earType: string
): string => getHearingAidWarrantyStart(item, earType);

/**
 * Gets the warranty end date from a line item, checking multiple possible locations.
 */
export const getHearingAidWarrantyEnd = (
  item: LineItem | undefined,
  earType: string
): string => {
  if (!item) return "";

  const patientProducts = (item as any).patient_products;

  // Check item-level display field first
  if ((item as any).manufacturer_warranty_expiration_date_display) {
    return formatDateOnlyValue(
      (item as any).manufacturer_warranty_expiration_date_display,
      "MM/dd/yyyy"
    );
  }

  // Check patient_products array
  if (Array.isArray(patientProducts)) {
    for (const pp of patientProducts) {
      if (
        getPatientProductEar(pp) === earType &&
        pp.inventory_product?.manufacturer_warranty_expiration_date
      ) {
        return formatDateOnlyValue(
          pp.inventory_product.manufacturer_warranty_expiration_date,
          "MM/dd/yyyy"
        );
      }
    }
  }

  // Check direct inventory_product
  if (item.inventory_product?.manufacturer_warranty_expiration_date) {
    return formatDateOnlyValue(
      item.inventory_product.manufacturer_warranty_expiration_date,
      "MM/dd/yyyy"
    );
  }

  return "";
};

/**
 * Gets the loss & damage warranty expiration date from a line item, checking the same
 * locations (and in the same order) as getHearingAidWarrantyEnd.
 */
export const getHearingAidLossDamageWarrantyEnd = (
  item: LineItem | undefined,
  earType: string
): string => {
  if (!item) return "";

  const patientProducts = (item as any).patient_products;

  // Check item-level display field first
  if (item.loss_damage_warranty_expiration_date_display) {
    return formatDateOnlyValue(
      item.loss_damage_warranty_expiration_date_display,
      "MM/dd/yyyy"
    );
  }

  // Check patient_products array
  if (Array.isArray(patientProducts)) {
    for (const pp of patientProducts) {
      if (
        getPatientProductEar(pp) === earType &&
        pp.inventory_product?.loss_damage_warranty_expiration_date
      ) {
        return formatDateOnlyValue(
          pp.inventory_product.loss_damage_warranty_expiration_date,
          "MM/dd/yyyy"
        );
      }
    }
  }

  // Check direct inventory_product
  if (item.inventory_product?.loss_damage_warranty_expiration_date) {
    return formatDateOnlyValue(
      item.inventory_product.loss_damage_warranty_expiration_date,
      "MM/dd/yyyy"
    );
  }

  return "";
};

/**
 * Gets the service plan expiration date from a line item. Unlike the warranty dates
 * this lives only on the patient product (there is no inventory-product equivalent),
 * so the ear-matched patient product is preferred and the first one is the fallback —
 * the same source the configurable invoice table uses (getServicePlanExpValue).
 */
export const getHearingAidServicePlanExpiration = (
  item: LineItem | undefined,
  earType: string
): string => {
  if (!item) return "";

  const patientProducts = (item as any).patient_products;
  if (!Array.isArray(patientProducts)) return "";

  const earMatched = patientProducts.find(
    (pp) =>
      getPatientProductEar(pp) === earType && pp.service_plan_expiration_date
  );
  const date =
    earMatched?.service_plan_expiration_date ??
    patientProducts[0]?.service_plan_expiration_date;

  return date ? formatDateOnlyValue(date, "MM/dd/yyyy") : "";
};

/**
 * Combines a left and a right per-ear value into the single "binaural" string the
 * Patient.SelectedInvoice.HearingAids.* tags render:
 *   - both present and equal  → the value once
 *   - both present, different → "L: <left> R: <right>"
 *   - one side only           → "L: <left>" / "R: <right>"
 *   - neither                 → "" (an empty string, NOT the select-invoice placeholder —
 *                                the caller decides whether an invoice is selected)
 */
export const combineBinauralValues = (
  leftValue: string | null | undefined,
  rightValue: string | null | undefined
): string => {
  const left = leftValue || null;
  const right = rightValue || null;

  if (left && right && left === right) return left;
  if (left && right) return `L: ${left} R: ${right}`;
  if (left) return `L: ${left}`;
  if (right) return `R: ${right}`;
  return "";
};
